import { FC, FormEvent, ReactElement, useEffect, useState } from 'react';
import moment from 'moment';
import classNames from 'classnames';
import { isNil } from 'lodash';
import { useSelector } from 'react-redux';
import { useBoolean } from '@fluentui/react-hooks';
import {
  Checkbox,
  DatePicker,
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
  Dropdown,
  IColumn,
  IconButton,
  IDropdownOption,
  MaskedTextField,
  MessageBarType,
  PrimaryButton,
  Text,
  TextField,
  TooltipHost,
  ComboBox,
} from '@fluentui/react';

import useNotifications from '../../../../../../hooks/useNotifications';
import apiService from '../../../../../../api';
import { useUserPermissions } from '../../../../../../hooks/useUserPermissions';
import { sortOrder } from '../../../../../../consts/sortOrder';
import { auth_tireChange_deleteButton, auth_tireChange_postBackdatedChanges } from '../../../../../../consts/programKeys';
import { customerSelector, locationSelector } from '../../../../../../redux/recordKeepingSlice';

import PrintingModal from '../../../../../PrintingModal/PrintingModal';
import ErrorsModal from '../../../../../ErrorsModal/ErrorsModal';
import LoadingScreen from '../../../../../LoadingScreen/LoadingScreen';
import SeparatorGy from '../../../../../SeparatorGy/SeparatorGy';
import Pagination from '../../../../../Pagination/Pagination';
import { IPaginationProps } from '../../../../../Pagination/IPaginationProps';
import { downloadFile, printingTypes } from '../../../../../PrintingModal/consts';

import { IPostBackdatedChangesState } from './IPostBackdatedChangesState';
import { columns } from './consts';
import { bulkLimit, pageSizes } from '../../../../../../consts/recordKeeping';
import { IPostBackdatedChangesProps } from './IPostBackdatedChangesProps';

import styles from './PostBackdatedChanges.module.scss';
import { IPostByB73Format } from '../PostByB73Format/IPostByB73Format';
import { emptyB73Format } from '../PostByB73Format/consts';
import { transformDate } from '../../../../../../shared/transformDate';
import { ITireChangeBackdatedChangesDto } from './ITireChangeBackdatedChangesDto';
import { ITireChangeB73FormatSaveInputDto } from './ITireChangeB73FormatSaveInputDto';

const PostBackdatedChanges: FC<IPostBackdatedChangesProps> = (): ReactElement => {
  const [state, setState] = useState<IPostBackdatedChangesState>({
    loading: false,
    items: [],
    selectedItems: [],
    foundCount: 0,
  });
  const [paginationProps, setPaginationProps] = useState<IPaginationProps>({
    total: 0,
    current: 1,
    onChangePage: (newPage: number) =>
      setPaginationProps((prev: any) => ({ ...prev, current: newPage })),
  });

  const [countOnPage, setCountOnPage] = useState<IDropdownOption>({
    key: pageSizes[0],
    text: pageSizes[0].toString(),
  });
  const { addNotification } = useNotifications();
  const { id: customerId, customerName } = useSelector(customerSelector);
  const { id: locationId, locationName } = useSelector(locationSelector);

  const [showPrintExport, { toggle: toggleShowPrintExport }] = useBoolean(false);
  const [showErrorsModal, { toggle: toggleShowErrorsModal }] = useBoolean(false);
  const [isDeletingDialogVisible, { toggle: toggleDeletingConfirmation }] = useBoolean(false);
  const [editedRowId, setEditedRowId] = useState<number>(-1);
  const [columnsState, setColumnsState] = useState<Array<any>>(columns);
  const [defaults, setDefaults] = useState<any>({ pfx: '', date: null });
  const [useDefaults, { toggle: toggleUseDefaults }] = useBoolean(false);
  const [parsedErrors, setParsedErrors] = useState<any>([]);
  const [tireToBeAdded, setTireToBeAdded] = useState<IPostByB73Format>(emptyB73Format);
  const [postTableId, setPostTableId] = useState<any>(null);
  const [reasonForRemovalList, setReasonForRemovalList] = useState<any[]>([
    { key: 'DAMAGED', text: 'DAMAGED' },
    { key: 'STOCK', text: 'STOCK' },
    { key: 'WORNOUT', text: 'WORNOUT' },
  ]);
  const [runningStatus, setRunningStatus] = useState<string>('RUNNING');
  const [initialLoad, setInitialLoad] = useState<boolean>(true);
  const [previousRunningStatus, setPreviousRunningStatus] = useState<string>('IDLE');
  const [levelOfService, setLevelOfService] = useState<string>('');


  const { hasPermission } = useUserPermissions();
  const userPermissions = hasPermission(auth_tireChange_postBackdatedChanges);
  const deletePermission = hasPermission(auth_tireChange_deleteButton);

  const onChangeDefaultsPrefix = (ev: FormEvent<HTMLInputElement | HTMLTextAreaElement>, pfx: string | undefined) => {
    setDefaults((prev: any) => ({ ...prev, pfx: pfx?.toUpperCase() }));
  };

  const updateAddValues = (defaultValues: any, field: string, value: string | boolean | undefined) => {
    defaultValues[field] = value;
    return defaultValues;
  };

  const updateRowsData = (currentState: any, field: string, value: string | undefined) => {
    return currentState.map((item: { [x: string]: string | undefined; id: any; }) => {
      if (editedRowId === item.id) {
        item[field] = value;
      }
      return item;
    });
  };

  const setOnPrefixTireToBeEdited = (ev: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, onPrefix: string | undefined) => {
    setState(prev => ({ ...prev, items: updateRowsData([...state.items], 'onPrefix', onPrefix?.toUpperCase()) }));
  };
  const setOnBrandNumberTireToBeEdited = (ev: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, onBrandNumber: string | undefined) => {
    setState(prev => ({ ...prev, items: updateRowsData([...state.items], 'onBrandNumber', onBrandNumber?.toUpperCase()) }));
  };
  const setOnSuffixTireToBeEdited = (ev: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, onSuffix: string | undefined) => {
    setState(prev => ({ ...prev, items: updateRowsData([...state.items], 'onSuffix', onSuffix?.toUpperCase()) }));
  };
  const setOnCotTireToBeEdited = (ev: FormEvent<HTMLInputElement | HTMLTextAreaElement>, onCot: string | undefined) => {
    setState(prev => ({ ...prev, items: updateRowsData([...state.items], 'onCot', onCot?.toUpperCase()) }));
  };
  const setActualTreadDepthTireToBeEdited = (ev: FormEvent<HTMLInputElement | HTMLTextAreaElement>, actualTreadDepth: string | undefined) => {
    setState(prev => ({ ...prev, items: updateRowsData([...state.items], 'actualTreadDepth', actualTreadDepth) }));
  };
  const setReasonForRemovalTireToBeEdited = (reasonForRemoval: string | undefined) => {
    setState(prev => ({ ...prev, items: updateRowsData([...state.items], 'reasonForRemoval', reasonForRemoval) }));
  };

  const validateAddTire = () => {
    const errors: any[] = [];
    if (levelOfService === 'FULL' && !tireToBeAdded.reasonForRemoval?.trim()) {
      errors.push({ 'index': 0, 'id': 'null', 'field': 'reasonForRemoval', 'message': 'Reason for Removal is required' });
    }
    if (levelOfService === 'FULL' && !tireToBeAdded.actualTreadDepth) {
      errors.push({ 'index': 0, 'id': 'null', 'field': 'actualTreadDepth', 'message': 'Actual Tread Depth is required' });
    }
    return errors;
  };

  const buildAddFormState = () =>
    useDefaults
      ? {
        ...emptyB73Format,
        chgDate: moment(defaults.date).format('MM/DD/YYYY'),
        offPrefix: defaults.pfx,
        onPrefix: defaults.pfx,
      }
      : { ...emptyB73Format };

  const handleAddTire = async (ev: React.FormEvent<HTMLFormElement>) => {
    ev.preventDefault();

    const validationErrors = validateAddTire();
    if (validationErrors.length > 0) {
      setParsedErrors(validationErrors);
      addNotification({
        text: 'Please fill in all required fields.',
        type: MessageBarType.error,
      });
      return;
    }
    
    const payload = {
      ...tireToBeAdded,
      chgDate: parseNullableDate(tireToBeAdded.chgDate),
      vNo: parseNullableString(tireToBeAdded.vNo),
      offPrefix: parseNullableString(tireToBeAdded.offPrefix),
      offBrandNumber: parseNullableDecimal(tireToBeAdded.offBrandNumber),
      offSuffix: parseNullableString(tireToBeAdded.offSuffix),
      onPrefix: parseNullableString(tireToBeAdded.onPrefix),
      onBrandNumber: parseNullableDecimal(tireToBeAdded.onBrandNumber),
      onSuffix: parseNullableString(tireToBeAdded.onSuffix),
      offCot: parseNullableBool(tireToBeAdded.offCot),
      onCot: parseNullableBool(tireToBeAdded.onCot),
      submit: parseNullableBool(tireToBeAdded.submit),
      wPos: parseNullableString(tireToBeAdded.wPos),
      actualTreadDepth: parseNullableDecimal(tireToBeAdded.actualTreadDepth),
      reasonForRemoval: parseNullableString(tireToBeAdded.reasonForRemoval),
    };

    try {
      setState(prev => ({ ...prev, loading: true }));

      await apiService.postBackdatedChanges.save([payload], customerId, locationId);

      addNotification({
        text: 'Tire was successfully added.',
        type: MessageBarType.success,
      });

      if (useDefaults) {
        setTireToBeAdded({
          ...emptyB73Format,
          chgDate: moment(defaults.date).format('MM/DD/YYYY'),
          offPrefix: defaults.pfx,
          onPrefix: defaults.pfx,
        });
      } else {
        setTireToBeAdded(emptyB73Format);
      }
      setTireToBeAdded(buildAddFormState());
      setParsedErrors([]);
      await fetchPostBackdatedChanges();
    } catch (e: any) {      
      const { response } = e;
      const errors = getApiErrors(response);

      if (handleApiErrors(errors)) {
        return;
      }

      addNotification({
        text: `Saving error: ${response?.data?.message || 'Unknown error'}`,
        type: MessageBarType.error,
      });
    } finally {
      setState((prev: any) => ({ ...prev, loading: false }));
      document.getElementById('chgDate')?.focus();
    }
  };

  const handleRelease = async () => {
    const itemsForRelease: ITireChangeB73FormatSaveInputDto[] = state.selectedItems
      .map((selectedItem: any) =>
        state.items.find((item: any) => item.id === selectedItem),
      )
      .filter(Boolean)
      .map((item: any): ITireChangeB73FormatSaveInputDto => ({
        id: parseNullableString(item.id),
        vNo: parseNullableString(item.vNo ?? item.vno),
        wPos: parseNullableString(item.wPos),
        offPrefix: parseNullableString(item.offPrefix),
        offBrandNumber: parseNullableStringDecimal(item.offBrandNumber),
        offSuffix: parseNullableString(item.offSuffix),
        onPrefix: parseNullableString(item.onPrefix),
        onBrandNumber: parseNullableStringDecimal(item.onBrandNumber),
        onSuffix: parseNullableString(item.onSuffix),
        onCot: parseNullableStringBool(item.onCot),
        offCot: parseNullableStringBool(item.offCot),
        submit: parseRequiredBool(item.submit, false),
        chgDate: parseNullableDate(item.chgDate),
        response: parseNullableBool(item.response),
        reason: parseNullableString(item.reason),
        tcBy: parseNullableString(item.tcBy),
        comments: parseNullableString(item.comments),
        actualTreadDepth: parseNullableDecimal(item.actualTreadDepth),
        reasonForRemoval: parseNullableString(item.reasonForRemoval),
      }));

    try {
      setState(prev => ({ ...prev, loading: true }));

      await apiService.postBackdatedChanges.release(itemsForRelease, customerId, locationId);

      addNotification({
        text: 'Backdated tires were successfully released.',
        type: MessageBarType.success,
      });
      if (paginationProps.current === 1) {
        await fetchPostBackdatedChanges();
      } else {
        await paginationProps.onChangePage(1);
      }
    } catch (e: any) {
      setState((prev: any) => ({ ...prev, loading: false }));
      const { response } = e;
      const locationId = response.data?.state?.filter((error: any) => error.field == 'locationId');
      switch (response.status) {
        case 400:
          setParsedErrors(response.data.state);
          if (locationId.length) {
            return addNotification({
              text: 'Location must not be empty',
              type: MessageBarType.error,
            });
          }
          return addNotification({
            text: `Backdated releasing error: ${response.data.state[0].message}`,
            type: MessageBarType.error,
          });
        case 499:
          return addNotification({
            text: `Backdated releasing error: ${response.data.state[0].message}`,
            type: MessageBarType.error,
          });
        default:
          addNotification({
            text: `Backdated releasing error: ${response.data.message}`,
            type: MessageBarType.error,
          });
      }
    }
  };

  const handleReleaseCondition = async () => {
    if (state.selectedItems.length > bulkLimit) { //currently set to 20
      handleReleaseBulkData();
    } else {
      handleRelease();
    }
  };

  const handleReleaseBulkData = async () => {
    try {
      setState(prev => ({ ...prev, loading: true }));
      const itemsForSave: Array<any> = [];
      state.selectedItems.map(selectedItem => {
        itemsForSave.push(state.items.find(item => item.id == selectedItem));
      });
      for (const item of itemsForSave) {
        if (item.offCot === 'Y') {
          item.offCot = true;
        } else if (item.offCot === 'N') {
          item.offCot = false;
        }
      
        if (item.onCot === 'Y') {
          item.onCot = true;
        } else if (item.onCot === 'N') {
          item.onCot = false;
        }
      }
      const updatedDataToBulkRelease = itemsForSave.map(item => ({
        ...item,
        actualTreadDepth: item.actualTreadDepth ? Number.parseInt(item.actualTreadDepth, 10) : null,
      }));
      const filename = `${customerId}${locationId || ''}${Date.now()}postByBackdatedChanges.txt`;
      const blob = new Blob([JSON.stringify(updatedDataToBulkRelease)], { type: 'application/json' });
      const file = new File([blob], filename);
      await apiService.postBackdatedChanges.bulkReleaseData(customerId, locationId, file);
      setRunningStatus('RUNNING');
      if (paginationProps.current === 1) {
        await fetchPostBackdatedChanges();
      } else {
        await paginationProps.onChangePage(1);
      }
    } catch (e: any) {
      setState((prev: any) => ({ ...prev, loading: false }));
      const { response } = e;
      const locationId = response.data?.state?.filter((error: any) => error.field == 'locationId');
      switch (response.status) {
        case 400:
          setParsedErrors(response.data.state);
          if (locationId.length) {
            return addNotification({
              text: 'Location must not be empty',
              type: MessageBarType.error,
            });
          }
          return addNotification({
            text: `Backdated releasing error: ${response.data.state[0].message}`,
            type: MessageBarType.error,
          });
        case 499:
          return addNotification({
            text: `Backdated releasing error: ${response.data.state[0].message}`,
            type: MessageBarType.error,
          });
        default:
          addNotification({
            text: `Backdated releasing error: ${response.data.message}`,
            type: MessageBarType.error,
          });
      }
    }
  };


  const fetchBulkReleaseStatus = async () => {
    try {
      const { data: { runStatus, errorMsg } }: any = await apiService.postBackdatedChanges.getBulkReleaseStatus(
        customerId,
        locationId,
      );
      if (runStatus == 'COMPLETED'){
        addNotification({ text: 'Backdated Bulk submit Completed Successfully', type: MessageBarType.success });
        setPreviousRunningStatus('IDLE');
        fetchPostBackdatedChanges();
      }
      else if (runStatus == 'RUNNING'){
        addNotification({ text: 'Backdated Bulk submit in Progress', type: MessageBarType.success });
        setRunningStatus('RUNNING');
        if (previousRunningStatus === 'IDLE'){
          setPreviousRunningStatus('RUNNING');
        }
      } 
      else if (runStatus == 'IDLE'){
        setRunningStatus('IDLE');
        if (previousRunningStatus === 'RUNNING'){
          addNotification({ text: 'Bulk submit Completed Successfully', type: MessageBarType.success });
          setPreviousRunningStatus('IDLE');
          fetchPostBackdatedChanges();
        }
      }
      else if (runStatus == 'FAILED'){
        if (!initialLoad){
          addNotification({ text: 'Bulk submit Failed: ' + errorMsg, type: MessageBarType.error });
          setPreviousRunningStatus('IDLE');
          fetchPostBackdatedChanges();
        }
        setRunningStatus('IDLE');
      }     
      if (initialLoad && state.items.length > 0){
        setInitialLoad(false);
      }
    } catch (e: any) {
      addNotification({ text: 'Error:' + e, type: MessageBarType.error });
      setRunningStatus('IDLE');
      fetchPostBackdatedChanges();
    }
  };

  const fetchReasonForRemovalList = async () => { // to fetch reason for removal list from API
    try {
      setState(prev => ({ ...prev, loading: true }));
      const { data }: any = await apiService.postByFootprint.getReasonForRemovalList();
      const reasonForRemovalOptions = data.reasonForRemoval.map((item: string) => ({ key: item, text: item }));
      setReasonForRemovalList(reasonForRemovalOptions.length > 0 ? reasonForRemovalOptions : reasonForRemovalList);
    } catch (e: any) {
      const { response } = e;
      addNotification({
        text: `Reason for removal list fetching error: ${response.data.message}`,
        type: MessageBarType.error,
      });

    } finally {
      setState(prev => ({ ...prev, loading: false }));
    }
  };

  const fetchLevelOfService = async () => { // to fetch level of service from API
    try {
      setState(prev => ({ ...prev, loading: true }));
      const { data }: any = await apiService.customerAPI.getKeyCharacteristics(customerId);
      setLevelOfService(data.levelOfService);      
    } catch (e: any) {
      const { response } = e;
      addNotification({
        text: `Fetching level of service error: ${response.data.message}`,
        type: MessageBarType.error,
      });
      setLevelOfService('');
    } finally {
      setState(prev => ({ ...prev, loading: false }));
    }
  };

  const onChangeCountOnPage = (
    event: FormEvent<HTMLDivElement>,
    item: IDropdownOption<any> | undefined,
  ): void => {
    setPaginationProps((prev: any) => ({ ...prev, current: 1 }));
    item && setCountOnPage(item);
  };

  const handleDelete: any = async () => {
    toggleDeletingConfirmation();
    try {
      setState(prev => ({ ...prev, loading: true }));
      await apiService.postBackdatedChanges.delete(state.selectedItems);
      addNotification({
        text: 'Selected tire(s) were successfully deleted.',
        type: MessageBarType.success,
      });
      if (paginationProps.current === 1) {
        await fetchPostBackdatedChanges();
      } else {
        await paginationProps.onChangePage(1);
      }
    } catch (e: any) {
      const { response } = e;
      setState(prev => ({ ...prev, loading: false }));
      addNotification({
        text: `Backdated deleting error: ${response.data.message}`,
        type: MessageBarType.error,
      });
    }
  };

  const getSortOrder = () => {
    const { fieldName, isSortedDescending } = columnsState.find(
      ({ isSorted }) => isSorted,
    );
    return {
      column: fieldName,
      order: isSortedDescending ? sortOrder.DESC : sortOrder.ASC,
    };
  };

  interface IParsedApiError {
    field?: string;
    message: string;
  }

  const getApiErrors = (response: any): IParsedApiError[] => {
    if (Array.isArray(response?.data)) {
      return response.data.map((err: any) => ({
        field: err.propertyName,
        message: err.errorMessage,
      }));
    }

    if (Array.isArray(response?.data?.state)) {
      return response.data.state;
    }

    return [];
  };

  const handleApiErrors = (errors: IParsedApiError[]): boolean => {
    if (!errors.length) {
      return false;
    }

    setParsedErrors(errors);

    const locationError = errors.find((err) => err.field === 'locationId');
    if (locationError) {
      addNotification({
        text: 'Location must not be empty',
        type: MessageBarType.error,
      });
      return true;
    }

    const errorMessage = errors.map((err) => err.message).join('\n');

    addNotification({
      text: errorMessage,
      type: MessageBarType.error,
    });

    return true;
  };

  const parseNullableStringBool = (value: unknown): string | null => {
    if (value === null || value === undefined || value === '') return null;

    if (typeof value === 'boolean') {
      return value ? 'true' : 'false';
    }

    if (typeof value === 'string') {
      const upper = value.trim().toUpperCase();
      if (upper === 'Y' || upper === 'TRUE') return 'true';
      if (upper === 'N' || upper === 'FALSE') return 'false';
    }

    return null;
  };

  const parseNullableStringDecimal = (value: unknown): string | null => {
    if (value === null || value === undefined) return null;

    if (typeof value === 'number') {
      return Number.isNaN(value) ? null : value.toString();
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return null;

      const normalized = trimmed.replaceAll(',', '');
      const parsed = Number(normalized);

      return Number.isNaN(parsed) ? null : parsed.toString();
    }

    return null;
  };

  const parseRequiredBool = (value: unknown, defaultValue = false): boolean => {
    const parsed = parseNullableBool(value);
    return parsed ?? defaultValue;
  };

  const parseNullableDecimal = (value: unknown): number | null => {
    if (value === null || value === undefined) return null;

    if (typeof value === 'number') {
      return Number.isNaN(value) ? null : value;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return null;

      const normalized = trimmed.replaceAll(',', '');
      const parsed = Number(normalized);

      return Number.isNaN(parsed) ? null : parsed;
    }

    return null;
  };

  const parseNullableBool = (value: unknown): boolean | null => {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'boolean') return value;

    if (typeof value === 'string') {
      const upper = value.trim().toUpperCase();
      if (upper === 'Y' || upper === 'TRUE') return true;
      if (upper === 'N' || upper === 'FALSE') return false;
    }

    return null;
  };

  const parseNullableDate = (value: unknown): string | null => {
    if (value === null || value === undefined) return null;

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return null;

      return /\d{2}\/\d{2}\/\d{4}/.test(trimmed)
        ? transformDate(trimmed)
        : trimmed;
    }

    return null;
  };

  const parseNullableString = (value: unknown): string | null => {
    if (value === null || value === undefined) return null;

    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed === '' ? null : trimmed;
    }

    if (
      typeof value === 'number' ||
      typeof value === 'boolean' ||
      typeof value === 'bigint'
    ) {
      const str = `${value}`.trim();
      return str === '' ? null : str;
    }

    return null;
  };

  const isEmpty = (val: any) => !val || !String(val).trim();

  const validateRow = (item: any) => {
    const errors: any[] = [];
    if (levelOfService === 'FULL' && isEmpty(item.reasonForRemoval)) {
      errors.push({
        'index': 0,
        'id': item.id,
        'field': 'reasonForRemoval',
        'message': 'Reason for Removal is required',
      });
    }
    if (levelOfService === 'FULL' && isEmpty(item.actualTreadDepth)) {
      errors.push({
        'index': 0,
        'id': item.id,
        'field': 'actualTreadDepth',
        'message': 'Actual Tread Depth is required',
      });
    }
    if (item.actualTreadDepth && (item.actualTreadDepth < 0 || item.actualTreadDepth > 32)) {
      errors.push({
        'index': 0,
        'id': item.id,
        'field': 'actualTreadDepth',
        'message': 'Must be between 0 and 32',
      });
    }
    return errors;
  };
  
  const handleSave = async () => {
    const itemsForSave: ITireChangeBackdatedChangesDto[] = state.selectedItems
      .map((selectedItem: any) =>
        state.items.find((item: any) => item.id === selectedItem),
      )
      .filter(Boolean)
      .map((item: any): ITireChangeBackdatedChangesDto => ({
        id: parseNullableString(item.id),
        chgDate: parseNullableDate(item.chgDate),
        vno: parseNullableString(item.vno),
        wPos: parseNullableString(item.wPos),
        offPrefix: parseNullableString(item.offPrefix),
        offBrandNumber: parseNullableDecimal(item.offBrandNumber),
        offSuffix: parseNullableString(item.offSuffix),
        offCot: parseNullableBool(item.offCot),
        onPrefix: parseNullableString(item.onPrefix),
        onBrandNumber: parseNullableDecimal(item.onBrandNumber),
        onSuffix: parseNullableString(item.onSuffix),
        onCot: parseNullableBool(item.onCot),
        reason: parseNullableString(item.reason),
        tcBy: parseNullableString(item.tcBy),
        submit: parseNullableBool(item.submit),
        comments: parseNullableString(item.comments),
        response: parseNullableBool(item.response),
        actualTreadDepth: parseNullableDecimal(item.actualTreadDepth),
        reasonForRemoval: parseNullableString(item.reasonForRemoval),
      }));

    let allErrors: any[] = [];
    for (const item of itemsForSave) {
      const rowErrors = validateRow(item);
      allErrors = [...allErrors, ...rowErrors];
    }
    if (allErrors.length > 0) {
      setParsedErrors(allErrors);
      addNotification({
        text: 'Please fix validation errors before saving.',
        type: MessageBarType.error,
      });
      return;
    } 

    try {
      setState(prev => ({ ...prev, loading: true }));

      await apiService.postBackdatedChanges.save(itemsForSave, customerId, locationId);

      addNotification({
        text: 'Post Backdated tires were successfully saved.',
        type: MessageBarType.success,
      });

      await fetchPostBackdatedChanges();
      setParsedErrors([]);
    } catch (e: any) {      
      const { response } = e;
      const errors = getApiErrors(response);

      if (handleApiErrors(errors)) {
        return;
      }

      addNotification({
        text: `Saving error: ${response?.data?.message || 'Unknown error'}`,
        type: MessageBarType.error,
      });

      await fetchPostBackdatedChanges();
    } finally {
      setState((prev: any) => ({ ...prev, loading: false }));
    }
  };

  const fetchPostBackdatedChanges = async () => {
    setState((prev) => ({ ...prev, loading: true }));
    try {
      const sortOrder = getSortOrder();
      const { data }: any = await apiService.postBackdatedChanges.get(
        { pageNumber: paginationProps.current, pageSize: +countOnPage.key },
        sortOrder,
        customerId,
        locationId,
      );
      const foundCount = data.total.found;
      const items = data.data.map((item: { chgDate: string; onCot: string; offCot: string; }) => {
        item.chgDate = moment(item.chgDate).format('MM/DD/YYYY');
        item.onCot = item.onCot ? 'Y' : 'N';
        item.offCot = item.offCot ? 'Y' : 'N';
        return item;
      });
      if (postTableId) {
        setState((prev: any) => ({ ...prev, items, foundCount, selectedItems: [items.find((item: any) => item.id == postTableId)?.id] }));
        setPostTableId(null);
      } else {
        setState((prev: any) => ({ ...prev, items, foundCount, selectedItems: [] }));
      }
      setPaginationProps((prev: any) => ({
        ...prev,
        total: Math.ceil(foundCount / +countOnPage.key),
      }));
      await fetchBulkReleaseStatus();
      setState((prev: any) => ({ ...prev, loading: false }));
    } catch (e: any) {
      addNotification({
        text: `Backdated fetching error: ${e?.message}`,
        type: MessageBarType.error,
      });
      setState((prev: any) => ({ ...prev, loading: false }));
    }
  };

  const renderLargeLabel = (props: any) => (
    <Text variant="large" className={styles.highlight}>{props.label}</Text>
  );

  const handleSelect = (e: any, itemId: any) => {
    if (e.target.checked) {
      setState((prev: any) => ({ ...prev, selectedItems: [...state.selectedItems, itemId] }));
    } else {
      setState((prev: any) => ({ ...prev, selectedItems: state.selectedItems.filter(row => row !== itemId) }));
    }
  };

  const handleSelectAll = (e: any, items: Array<any>) => {
    e.target.checked ? setState((prev: any) => ({ ...prev, selectedItems: items })) : setState((prev: any) => ({ ...prev, selectedItems: [] }));
  };

  const onEdit = (itemId: any) => {
    setEditedRowId(itemId);
    if (useDefaults) {
      const newItems = [...state.items].map(item => {
        if (item.id == itemId) {
          item.onPrefix = defaults.pfx;
          item.offPrefix = defaults.pfx;
          if (!isNil(defaults.date)) {
            item.changeDate = moment(defaults.date).format('MM/DD/YYYY');
          }
        }
        return item;
      });
      setState(prev => ({ ...prev, items: newItems }));
    }
  };

  const onColumnClick = (column: IColumn): void => {
    const newColumns: IColumn[] = [...columnsState];
    const currColumn: IColumn = newColumns.filter(currCol => column.key === currCol.key)[0];
    newColumns.forEach((newCol: IColumn) => {
      if (newCol === currColumn) {
        currColumn.isSortedDescending = !currColumn.isSortedDescending;
        currColumn.isSorted = true;
      } else {
        newCol.isSorted = false;
        newCol.isSortedDescending = true;
      }
    });

    setColumnsState(newColumns);
  };

  const parseErrors = (id: string | number | null, field: string) => {
    const customError = parsedErrors?.filter((error: { id: string; field: string; }) => error.id == id && error.field == field)[0];
    if (customError) {
      return customError.message;
    }
  };

  const highlightRowIfError = (id: any) => {
    if (parsedErrors.length > 0) {
      return parsedErrors.filter((error: { id: any; }) => error.id == id).length > 0;
    }
  };

  const handlePrint = async (printingType: any) => {
    setState(prev => ({ ...prev, loading: true }));
    toggleShowPrintExport();
    try {
      const sortOrder = getSortOrder();
      const requestData = {
        pagination: { pageNumber: paginationProps.current, pageSize: +countOnPage.key },
        sortOrder,
        customerId,
        locationId,
      };
      const headerFields = [
        { title: 'Customer Name', value: customerName },
        { title: 'Location Name', value: locationName },
      ];
      const { data }: any = printingType === printingTypes.excel ?
        await apiService.postBackdatedChanges.printExcel(requestData, headerFields) :
        await apiService.postBackdatedChanges.printPdf(requestData, headerFields);

      addNotification({
        text: 'File was successfully received.',
        type: MessageBarType.success,
      });
      downloadFile(data, printingType);
    } catch (e: any) {
      addNotification({
        text: 'Printing error',
        type: MessageBarType.error,
      });
    } finally {
      setState(prev => ({ ...prev, loading: false }));
    }
  };

  const searchRecordWithError = async (postTableId: any, response?: boolean) => {
    setState(prev => ({ ...prev, loading: true }));
    try {
      const sortOrder = getSortOrder();
      const { data }: any = await apiService.postBackdatedChanges.getPage(
        { pageNumber: paginationProps.current, pageSize: +countOnPage.key },
        sortOrder,
        customerId,
        locationId,
        postTableId,
      );
      setPostTableId(postTableId);
      if (data.pageNumber != paginationProps.current) {
        await paginationProps.onChangePage(data.pageNumber);
      } else {
        setState((prev: any) => ({ ...prev, selectedItems: [state.items.find((item: any) => item.id == postTableId)?.id] }));
        setState((prev) => ({ ...prev, items: prev.items.map(el => el.id === postTableId ? ({ ...el, response}) : el) }));
        setPostTableId(null);
      }
    } catch (e: any) {
      const { response } = e;
      addNotification({
        text: `B73 fetching error: ${response?.data?.message}`,
        type: MessageBarType.error,
      });
    } finally {
      setState((prev: any) => ({ ...prev, loading: false }));
    }
  };

  useEffect(() => {
    fetchPostBackdatedChanges();
  }, [paginationProps.current, countOnPage, columnsState, customerId, locationId]);

  useEffect(() => {
    if(useDefaults) {
      setTireToBeAdded((prev: any) => ({ ...prev, chgDate: moment(defaults.date).format('MM/DD/YYYY'), offPrefix: defaults.pfx, onPrefix: defaults.pfx }));
    } else {
      setTireToBeAdded((prev: any) => ({ ...prev, chgDate: '', offPrefix: '', onPrefix: '' }));
    }
  }, [useDefaults]);

  useEffect(() => {
    fetchReasonForRemovalList();
  }, []);

  useEffect(() => {
    fetchLevelOfService();
  }, []);

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;
    if (runningStatus === 'RUNNING') {
      intervalId = setInterval(() => {
        fetchBulkReleaseStatus();
      }, 5000); // 5 seconds
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [runningStatus]); 
  return (
    <>
      <div>
        <div className={styles.tableHeading}>
          <div>
            <Text variant="xLarge" className={styles.highlight}>Posted Tire Changes</Text>
            <SeparatorGy vertical />
            <Text variant="xLarge" className={styles.highlight}>{state.foundCount} found</Text>
          </div>
        </div>
        { (!userPermissions.isWrite || runningStatus == 'RUNNING') ?
          <Text variant="large" className={styles.highlight}>Input temporarily disabled due to ongoing process.</Text> : null
        }
        <div className={styles.tableHeading}>
          <form className={styles.tiresAddingBlock} onSubmit={handleAddTire}>
            <div className="ms-TextField">
              <label htmlFor="chgDate">ChgDate</label>
              <MaskedTextField
                id="chgDate"
                value={tireToBeAdded.chgDate}
                mask="99/99/9999"
                onChange={(e, value) => setTireToBeAdded(updateAddValues({ ...tireToBeAdded }, 'chgDate', value?.toUpperCase()))}
                errorMessage={parseErrors(null, 'chgDate')}
                autoFocus
                disabled={runningStatus === 'RUNNING'}
              />
            </div>
            <div className="ms-TextField">
              <label htmlFor="vNo_table">VNo</label>
              <TextField
                id="vNo_table"
                value={'' + tireToBeAdded.vNo}
                onChange={(e, value) => setTireToBeAdded(updateAddValues({ ...tireToBeAdded }, 'vNo', value?.toUpperCase()))}
                errorMessage={parseErrors('null', 'vno')}
                disabled={runningStatus === 'RUNNING'}
              />
            </div>
            <div className="ms-TextField">
              <label htmlFor="wPos_table">WPos</label>
              <TextField
                id="wPos_table"
                value={tireToBeAdded.wPos}
                onChange={(e, value) => setTireToBeAdded(updateAddValues({ ...tireToBeAdded }, 'wPos', value?.toUpperCase()))}
                errorMessage={parseErrors('null', 'wPos')}
                disabled={runningStatus === 'RUNNING'}
              />
            </div>
            <div className="ms-TextField">
              <label htmlFor="offPrefix_table">OffPrefix</label>
              <TextField
                id="offPrefix_table"
                value={tireToBeAdded.offPrefix}
                onChange={(e, value) => setTireToBeAdded(updateAddValues({ ...tireToBeAdded }, 'offPrefix', value?.toUpperCase()))}
                errorMessage={parseErrors('null', 'offPrefix')}
                disabled={runningStatus === 'RUNNING'}
              />
            </div>
            <div className="ms-TextField">
              <label htmlFor="offBrandNumber_table">OffBrandNumber</label>
              <TextField
                id="offBrandNumber_table"
                value={'' + tireToBeAdded.offBrandNumber}
                onChange={(e, value) => setTireToBeAdded(updateAddValues({ ...tireToBeAdded }, 'offBrandNumber', value?.toUpperCase()))}
                errorMessage={parseErrors('null', 'offBrandNumber')}
                disabled={runningStatus === 'RUNNING'}
              />
            </div>
            <div className="ms-TextField">
              <label htmlFor="offSuffix_table">OffSuffix</label>
              <TextField
                id="offSuffix_table"
                value={tireToBeAdded.offSuffix}
                onChange={(e, value) => setTireToBeAdded(updateAddValues({ ...tireToBeAdded }, 'offSuffix', value?.toUpperCase()))}
                errorMessage={parseErrors('null', 'offSuffix')}
                disabled={runningStatus === 'RUNNING'}
              />
            </div>
            <div className="ms-TextField">
              <label htmlFor="offCot_table">OffCOT</label>
              <TextField
                id="offCot_table"
                value={'' + tireToBeAdded.offCot}
                onChange={(e, value) => setTireToBeAdded(updateAddValues({ ...tireToBeAdded }, 'offCot', value?.toUpperCase()))}
                errorMessage={parseErrors('null', 'offCot')}
                disabled={runningStatus === 'RUNNING'}
              />
            </div>
            <div className="ms-TextField">
              <label htmlFor="onPrefix_table">OnPrefix</label>
              <TextField
                id="onPrefix_table"
                value={tireToBeAdded.onPrefix}
                onChange={(e, value) => setTireToBeAdded(updateAddValues({ ...tireToBeAdded }, 'onPrefix', value?.toUpperCase()))}
                errorMessage={parseErrors('null', 'onPrefix')}
                disabled={runningStatus === 'RUNNING'}
              />
            </div>
            <div className="ms-TextField">
              <label htmlFor="onBrandNumber_table">OnBrandNumber</label>
              <TextField
                id="onBrandNumber_table"
                value={'' + tireToBeAdded.onBrandNumber}
                onChange={(e, value) => setTireToBeAdded(updateAddValues({ ...tireToBeAdded }, 'onBrandNumber', value?.toUpperCase()))}
                errorMessage={parseErrors('null', 'onBrandNumber')}
                disabled={runningStatus === 'RUNNING'}
              />
            </div>
            <div className="ms-TextField">
              <label htmlFor="onSuffix_table">OnSuffix</label>
              <TextField
                id="onSuffix_table"
                value={tireToBeAdded.onSuffix}
                onChange={(e, value) => setTireToBeAdded(updateAddValues({ ...tireToBeAdded }, 'onSuffix', value?.toUpperCase()))}
                errorMessage={parseErrors('null', 'onSuffix')}
                disabled={runningStatus === 'RUNNING'}
              />
            </div>
            <div className="ms-TextField">
              <label htmlFor="onCot_table">OnCOT</label>
              <TextField
                id="onCot_table"
                value={'' + tireToBeAdded.onCot}
                onChange={(e, value) => setTireToBeAdded(updateAddValues({ ...tireToBeAdded }, 'onCot', value?.toUpperCase()))}
                errorMessage={parseErrors('null', 'onCot')}
                disabled={runningStatus === 'RUNNING'}
              />
            </div>
            <div className="ms-TextField">
              <label htmlFor="actualTreadDepth_table">Actual Tread Depth</label>
              <TooltipHost content="Max tread depth: 32" directionalHint={9}>
                <TextField
                  id="actualTreadDepth_table"
                  value={String(tireToBeAdded.actualTreadDepth || '')}
                  onChange={(e, value) => {
                    if (!value) {
                      setTireToBeAdded(updateAddValues({ ...tireToBeAdded }, 'actualTreadDepth', ''));
                      return;
                    }
                    if (!/^\d+$/.test(value)) return;
                    const num = Number.parseInt(value, 10);
                    if (num >= 0 && num <= 32) {
                      setTireToBeAdded(updateAddValues({ ...tireToBeAdded }, 'actualTreadDepth', value));
                    }
                  }}
                  errorMessage={parseErrors('null', 'actualTreadDepth')}
                  disabled={runningStatus === 'RUNNING'}
                />
              </TooltipHost>
            </div>
            <div className="ms-TextField">
              <label htmlFor="reasonForRemoval_table">Reason For Removal</label>
              <ComboBox
                id="reasonForRemoval_table"
                selectedKey={tireToBeAdded.reasonForRemoval ? tireToBeAdded.reasonForRemoval : null}
                text={tireToBeAdded.reasonForRemoval || ''}
                options={reasonForRemovalList}
                allowFreeform={true}
                autoComplete="on"
                onChange={(ev, option, _index, value) => {
                  const nextValue = option?.key ? String(option.key) : (value || '');
                  setTireToBeAdded(
                    updateAddValues(
                      { ...tireToBeAdded },
                      'reasonForRemoval',
                      nextValue.toUpperCase(),
                    ),
                  );
                }}
                errorMessage={parseErrors('null', 'reasonForRemoval')}
                disabled={runningStatus === 'RUNNING'}
                required={levelOfService === 'FULL'}
              />
            </div>
            <PrimaryButton
              disabled={!userPermissions.isWrite || runningStatus === 'RUNNING'}
              id="addButton"
              text="Add"
              type='submit'
            />
          </form>
        </div>
        <div className={styles.tableHeading}>
          <div className={styles.useDefaults}>
            <Checkbox id="useDefaults" label="Use Defaults:" onRenderLabel={renderLargeLabel} checked={useDefaults} onChange={toggleUseDefaults} />
            <div>
              <TextField id="prefixDefault" label="Ctrl Pfx" value={defaults.pfx} onChange={onChangeDefaultsPrefix} />
              <DatePicker
                id="dateDefault"
                showMonthPickerAsOverlay={true}
                label="MM/DD/YY"
                value={defaults.date}
                onSelectDate={(date: Date | null | undefined) => setDefaults((prev: any) => ({ ...prev, date }))}
                formatDate={(date: any) => moment(date).format('MM/DD/YYYY')}
              />
            </div>
          </div>
          <div>
            <Text variant="large" className={styles.highlight}>Show # of rows:&nbsp;</Text>
            <Dropdown
              options={pageSizes.map((pageSize) => ({
                key: pageSize,
                text: pageSize.toString(),
              }))}
              defaultSelectedKey={pageSizes[0]}
              selectedKey={countOnPage?.key}
              onChange={onChangeCountOnPage}
            />
            <SeparatorGy vertical />
            <Text variant="large" className={styles.highlight}>
              {state.selectedItems.length} items selected
            </Text>
            <SeparatorGy vertical />
            <IconButton
              id="toggleDeletingConfirmationButton"
              disabled={!(state.selectedItems.length && userPermissions.isWrite && deletePermission.isWrite)}
              iconProps={{ iconName: 'Delete' }}
              onClick={toggleDeletingConfirmation}
            />
          </div>
        </div>
        <div className={styles['table-wrapper']}>
          <table>
            <thead>
              <tr>
                {
                  columnsState.map(item => (
                    <th key={item.name} className={item.isSorted && item.isSortedDescending ? styles.descending : item.isSorted && !item.isSortedDescending ? styles.ascending : undefined} onClick={() => onColumnClick(item)}>{item.name}</th>
                  ))
                }
                <th>
                  <div className={styles.round}>
                    <input type="checkbox" id="all" checked={state.items.length !== 0 &&
                      (state.selectedItems.length === +countOnPage.key || state.selectedItems.length === state.items.length)} onChange={(e) => handleSelectAll(e, state.items.map(item => item.id))} />
                    <label htmlFor="all"></label>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {
                state.items.map((item, index) => (
                  item.submit ?
                    <tr key={item.id || index}>
                      <td>{item.chgDate && moment(item.chgDate).format('MM/DD/YYYY')}</td>
                      <td>{item.vno}</td>
                      <td>{item.wPos}</td>
                      <td>{item.offPrefix}</td>
                      <td>{item.offBrandNumber}</td>
                      <td>{item.offSuffix}</td>
                      <td>{item.offCot}</td>
                      <td>{item.onPrefix}</td>
                      <td>{item.onBrandNumber}</td>
                      <td>{item.onSuffix}</td>
                      <td>{item.onCot}</td>
                      <td>{item.actualTreadDepth}</td>
                      <td>{item.reasonForRemoval}</td>
                      <td>{item.submit ? 'Y' : 'N'}</td>
                      <td>
                        <div className={styles.round}>
                          <input type="checkbox" id={item.id} checked={state.selectedItems.includes(item.id)} onChange={(e) => handleSelect(e, item.id)} />
                          <label htmlFor={item.id}></label>
                        </div>
                      </td>
                    </tr> :
                    <tr
                      key={item.id}
                      className={classNames(state.selectedItems.includes(item.id) ? styles.trSelected : styles.trBasic,
                        highlightRowIfError(item.id) ? styles.trError : styles.trBasic)}
                      onFocus={() => onEdit(item.id)}
                    >
                      <td>{item.chgDate && moment(item.chgDate).format('MM/DD/YYYY')}</td>
                      <td>{item.vno}</td>
                      <td>{item.wPos}</td>
                      <td>{item.offPrefix}</td>
                      <td>{item.offBrandNumber}</td>
                      <td>{item.offSuffix}</td>
                      <td>{item.offCot}</td>
                      <td>
                        <TextField
                          id="onPrefix"
                          styles={{ fieldGroup: { border: '1px solid transparent' } }}
                          value={item.onPrefix}
                          onChange={setOnPrefixTireToBeEdited}
                          errorMessage={parseErrors(item.id, 'onPrefix')}
                          disabled={runningStatus === 'RUNNING'}
                        />
                      </td>
                      <td>
                        <TextField
                          id="onBrandNumber"
                          styles={{ fieldGroup: { border: '1px solid transparent' } }}
                          value={item.onBrandNumber}
                          onChange={setOnBrandNumberTireToBeEdited}
                          errorMessage={parseErrors(item.id, 'onBrandNumber')}
                          disabled={runningStatus === 'RUNNING'}
                        />
                      </td>
                      <td>
                        <TextField
                          id="onSuffix"
                          styles={{ fieldGroup: { border: '1px solid transparent' } }}
                          value={item.onSuffix}
                          onChange={setOnSuffixTireToBeEdited}
                          errorMessage={parseErrors(item.id, 'onSuffix')}
                          disabled={runningStatus === 'RUNNING'}
                        />
                      </td>
                      <td>
                        <TextField
                          id="onCot"
                          styles={{ fieldGroup: { border: '1px solid transparent' } }}
                          value={item.onCot}
                          onChange={setOnCotTireToBeEdited}
                          errorMessage={parseErrors(item.id, 'onCot')}
                          disabled={runningStatus === 'RUNNING'}
                        />
                      </td>
                      <td>
                        <TooltipHost content="Max tread depth: 32" directionalHint={9}>
                          <TextField
                            id="actualTreadDepth"
                            styles={{ fieldGroup: { border: '1px solid transparent' } }}
                            value={item.actualTreadDepth}
                            // onChange={setActualTreadDepthTireToBeEdited}
                            onChange={(e, value) => {
                              if (!value) {
                                setActualTreadDepthTireToBeEdited(e,'');
                                return;
                              }
                              if (!/^\d+$/.test(value)) return;
                              const num = Number.parseInt(value, 10);
                              if (num >= 0 && num <= 32) {
                                setActualTreadDepthTireToBeEdited(e, value);
                              }
                            }}
                            errorMessage={parseErrors(item.id, 'actualTreadDepth')}
                            disabled={runningStatus === 'RUNNING'}
                          />
                        </TooltipHost>
                      </td>
                      <td>
                        <ComboBox
                          id="reasonForRemoval"
                          styles={{ root: { border: '1px solid transparent', width: '120px' } }}
                          selectedKey={item.reasonForRemoval ? item.reasonForRemoval : null}
                          text={item.reasonForRemoval || ''}
                          options={reasonForRemovalList}
                          allowFreeform={true}
                          autoComplete="on"
                          onChange={(ev, option, _index, value) => {
                            const nextValue = option?.key ? String(option.key) : (value || '');
                            setReasonForRemovalTireToBeEdited(nextValue.toUpperCase());
                          }}
                          errorMessage={parseErrors(item.id, 'reasonForRemoval')}
                          disabled={runningStatus === 'RUNNING'}
                          required={levelOfService === 'FULL'}
                        />
                      </td>
                      <td>{item.submit ? 'Y' : 'N'}</td>
                      <td>
                        <div className={styles.round}>
                          <input type="checkbox" id={item.id} checked={state.selectedItems.includes(item.id)} onChange={(e) => handleSelect(e, item.id)} />
                          <label htmlFor={item.id}></label>
                        </div>
                      </td>
                    </tr>
                ))
              }
            </tbody>
          </table>
        </div>
        <SeparatorGy />
        <Pagination {...paginationProps} />
      </div>
      <Dialog
        hidden={!isDeletingDialogVisible}
        onDismiss={toggleDeletingConfirmation}
        dialogContentProps={{
          type: DialogType.normal,
          title: 'Confirmation',
          subText: `Are you sure you want to delete (${state.selectedItems.length}) items?`,
        }}
        modalProps={{ isBlocking: true }}
      >
        <DialogFooter>
          <PrimaryButton id="deleteButton" onClick={handleDelete} text="Delete" />
          <DefaultButton onClick={toggleDeletingConfirmation} text="Cancel" />
        </DialogFooter>
      </Dialog>
      <div className={classNames(styles.buttonsWrapper)}>
        <DefaultButton
          id="printButton"
          onClick={toggleShowPrintExport}
          text="Print/Export"
        />
        <DefaultButton
          id="viewErrorsButton"
          onClick={toggleShowErrorsModal}
          text="View Errors"
        />
        <PrimaryButton
          id="saveButton"
          text="Save"
          onClick={handleSave}
          disabled={!userPermissions.isWrite || !state.selectedItems.length || runningStatus === 'RUNNING'}
        />
        <PrimaryButton
          id="releaseButton"
          text="Release"
          onClick={handleReleaseCondition}
          disabled={!userPermissions.isWrite || !state.selectedItems.length || runningStatus === 'RUNNING'}
        />
      </div>
      <PrintingModal
        isOpened={showPrintExport}
        onClose={toggleShowPrintExport}
        onPrint={handlePrint}
      />
      <ErrorsModal
        isModalOpen={showErrorsModal}
        hideModal={toggleShowErrorsModal}
        fetchErrorsService={apiService.postBackdatedChanges.getErrors}
        searchError={searchRecordWithError}
        shouldConfirm={true}
      />
      {state.loading && <LoadingScreen />}
    </>
  );
};

export default PostBackdatedChanges;